import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  UserGroupIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

import { Card, CardHeader, CardTitle, CardContent } from "./ui/Card";
import { Button } from "./ui/Button";
import { Badge } from "./ui/Badge";
import { Modal } from "./ui/Modal";
import { ConfirmationModal } from "./ui/ConfirmationModal";
import { apiService } from "../services/api";
import { formatLocalDate } from "../utils/helpers";

const StaffAssignment = ({ session, availableStaff, onUpdate }) => {
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [staffToUnassign, setStaffToUnassign] = useState(null);
  const [unassigning, setUnassigning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAddEvaluatorForm, setShowAddEvaluatorForm] = useState(false);
  const [newEvaluator, setNewEvaluator] = useState({ name: "", email: "" });
  const [addingEvaluator, setAddingEvaluator] = useState(false);

  const loadAssignedStaff = useCallback(async () => {
    try {
      const assignments = await apiService.getSessionStaff(session.id);
      setAssignedStaff(assignments);
    } catch (error) {
      console.error("Error loading staff assignments:", error);
    }
  }, [session.id]);

  useEffect(() => {
    loadAssignedStaff();
  }, [loadAssignedStaff]);

  const handleAssignStaff = async (staffMember, role = "evaluator") => {
    setLoading(true);
    try {
      await apiService.assignStaffToSession(session.id, {
        user_id: staffMember.id,
        role: role,
      });

      toast.success(`${staffMember.name} assigned as ${role}`);
      await loadAssignedStaff();
      onUpdate();
    } catch (error) {
      if (error.message.includes("already assigned")) {
        toast.error("Staff member is already assigned to this session");
      } else {
        toast.error("Failed to assign staff member");
      }
    }
    setLoading(false);
  };

  const handleUnassignStaff = async () => {
    if (!staffToUnassign) return;

    setUnassigning(true);
    try {
      await apiService.unassignStaffFromSession(session.id, staffToUnassign.id);
      toast.success("Staff member unassigned");
      setShowUnassignModal(false);
      setStaffToUnassign(null);
      await loadAssignedStaff();
      onUpdate();
    } catch (error) {
      toast.error("Failed to unassign staff member");
    }
    setUnassigning(false);
  };

  const handleUnassignStaffClick = (assignment) => {
    setStaffToUnassign(assignment);
    setShowUnassignModal(true);
  };

  const handleAddEvaluator = async (e) => {
    e.preventDefault();
    if (!newEvaluator.name.trim() || !newEvaluator.email.trim()) {
      toast.error("Name and email are required");
      return;
    }

    setAddingEvaluator(true);
    try {
      const createdUser = await apiService.createEvaluatorByEmail({
        name: newEvaluator.name.trim(),
        email: newEvaluator.email.trim().toLowerCase(),
      });

      console.log("addUserToClub response:", {
        userId: createdUser.id,
        session: session,
      });
      debugger;

      await apiService.addUserToClub(createdUser.id, session.id, "evaluator");

      await apiService.assignStaffToSession(session.id, {
        user_id: createdUser.id,
        role: "evaluator",
      });

      toast.success(`${createdUser.name} added and assigned as evaluator`);
      setNewEvaluator({ name: "", email: "" });
      setShowAddEvaluatorForm(false);
      await loadAssignedStaff();
      onUpdate();
    } catch (error) {
      if (error.message.includes("already exists")) {
        toast.error("A user with this email already exists");
      } else if (error.message.includes("already assigned")) {
        toast.error("This evaluator is already assigned to the session");
      } else {
        toast.error("Failed to add evaluator");
      }
    }
    setAddingEvaluator(false);
  };

  const resetAddEvaluatorForm = () => {
    setNewEvaluator({ name: "", email: "" });
    setShowAddEvaluatorForm(false);
  };

  const availableUnassignedStaff = availableStaff.filter(
    (staff) => !assignedStaff.some((assigned) => assigned.user_id === staff.id),
  );

  const getRoleColor = (role) => {
    switch (role) {
      case "coordinator":
        return "blue";
      case "coach":
        return "green";
      case "evaluator":
        return "yellow";
      default:
        return "gray";
    }
  };

  const getRoleLabel = (role) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "green";
      case "completed":
        return "blue";
      case "scheduled":
        return "yellow";
      default:
        return "gray";
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>{session.name}</CardTitle>
              <div className="flex items-center space-x-2 mt-1">
                <Badge variant={getStatusColor(session.status)}>
                  {session.status}
                </Badge>
                <span className="text-sm text-gray-500">
                  {formatLocalDate(session.session_date)}
                </span>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setShowAssignModal(true)}
              disabled={
                session.status === "completed" ||
                availableUnassignedStaff.length === 0
              }
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Assign Staff
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Assigned Staff:</span>
              <span className="font-medium">
                {assignedStaff.length} member
                {assignedStaff.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="text-center">
                <div className="font-medium text-slate-600">
                  {assignedStaff.filter((s) => s.role === "coordinator").length}
                </div>
                <div className="text-gray-500">Coordinators</div>
              </div>
              <div className="text-center">
                <div className="font-medium text-green-600">
                  {assignedStaff.filter((s) => s.role === "coach").length}
                </div>
                <div className="text-gray-500">Coaches</div>
              </div>
              <div className="text-center">
                <div className="font-medium text-yellow-600">
                  {assignedStaff.filter((s) => s.role === "evaluator").length}
                </div>
                <div className="text-gray-500">Evaluators</div>
              </div>
            </div>
          </div>

          {assignedStaff.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">Assigned Staff</h4>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {assignedStaff.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-md text-sm"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 font-semibold text-xs">
                          {assignment.users.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="font-medium">
                        {assignment.users.name}
                      </span>
                      <Badge
                        variant={getRoleColor(assignment.role)}
                        className="text-xs"
                      >
                        {getRoleLabel(assignment.role)}
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleUnassignStaffClick(assignment)}
                      disabled={session.status === "completed"}
                    >
                      <TrashIcon className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Staff Assignment Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title="Assign Staff to Session"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <h4 className="font-medium text-slate-900">
              Session: {session.name}
            </h4>
            <p className="text-sm text-slate-700">
              {formatLocalDate(session.session_date)}
              {session.location && ` • ${session.location}`}
            </p>
          </div>

          {!showAddEvaluatorForm && (
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900">Available Staff</h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAddEvaluatorForm(true)}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Evaluator by Email
              </Button>
            </div>
          )}

          {showAddEvaluatorForm && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-slate-900">
                  Add New Evaluator
                </h4>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={resetAddEvaluatorForm}
                >
                  ×
                </Button>
              </div>
              <form onSubmit={handleAddEvaluator} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={newEvaluator.name}
                    onChange={(e) =>
                      setNewEvaluator((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                    placeholder="Enter evaluator's full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={newEvaluator.email}
                    onChange={(e) =>
                      setNewEvaluator((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                    placeholder="Enter evaluator's email address"
                    required
                  />
                </div>
                <div className="flex space-x-2">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={
                      addingEvaluator ||
                      !newEvaluator.name.trim() ||
                      !newEvaluator.email.trim()
                    }
                  >
                    {addingEvaluator
                      ? "Adding..."
                      : "Add & Assign as Evaluator"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={resetAddEvaluatorForm}
                    disabled={addingEvaluator}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {availableUnassignedStaff.length === 0 && !showAddEvaluatorForm ? (
            <div className="text-center py-8">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                All staff assigned
              </h3>
              <p className="mt-1 text-sm text-gray-500 mb-3">
                All available staff members are already assigned to this session
              </p>
              <Button size="sm" onClick={() => setShowAddEvaluatorForm(true)}>
                <PlusIcon className="w-4 h-4 mr-2" />
                Add New Evaluator
              </Button>
            </div>
          ) : availableUnassignedStaff.length > 0 && !showAddEvaluatorForm ? (
            <div className="space-y-3">
              {availableUnassignedStaff.map((staff) => (
                <div
                  key={staff.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-md"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        {staff.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{staff.name}</p>
                      <p className="text-sm text-gray-500">{staff.email}</p>
                    </div>
                    <Badge variant={getRoleColor(staff.role)}>
                      {getRoleLabel(staff.role)}
                    </Badge>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAssignStaff(staff, "evaluator")}
                      disabled={loading}
                    >
                      Evaluator
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleAssignStaff(staff, staff.role)}
                      disabled={loading}
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                      Assign
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Unassign Staff Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnassignModal}
        onClose={() => {
          setShowUnassignModal(false);
          setStaffToUnassign(null);
        }}
        onConfirm={handleUnassignStaff}
        title="Remove Staff Member"
        message={
          staffToUnassign
            ? `Remove ${staffToUnassign.users.name} from this session?`
            : "Remove this staff member from the session?"
        }
        confirmText="Remove"
        loading={unassigning}
      />
    </>
  );
};

export { StaffAssignment };
